import cookies from 'js-cookie'
import { pathOr } from 'ramda'
import { matchPath } from 'react-router-dom-v5-compat'
import { MiddlewareAPI } from 'redux'

import type { GetOrgBalanceApiResponse } from '@/__generated/es-api'
import { clientRef } from '@/app/client-ref'
import { cannot } from '@/app/helpers'
import { OTHER_UPDATED_CAMPAIGN } from '@/app/module/campaigns/definitions'
import { Export } from '@/app/module/exports/types'
import { createNotification } from '@/app/module/notifications/store/actions'
import { ERROR_EXPORT_CALL_RECORDINGS, ERROR_EXPORT_CAMPAIGN_REPORT } from '@/app/module/reports/definitions'
import { OrgReportMimeType } from '@/app/module/reports/types'
import requestSpecs from '@/app/module/request'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { captureError } from '@/app/service/util/error'
import { BackendFile } from '@/app/types'
import http from '@/config/http'

import { esApi } from './api'

const request = requestSpecs as any

export type CampaignUpdatedPayload = {
  type: 'campaign'
  campaign: {
    id: number
    name: string
    published: boolean
    open: boolean
    launched: boolean
    aborted: boolean
    archived: boolean
    deleted: boolean
    version: number
    clientRef?: string
  }
}

export type BalanceUpdatedPayload = {
  type: 'balance'
  balance: Partial<GetOrgBalanceApiResponse>
}

export type ExportDonePayload = {
  type: 'exportDone'
  export: Export
}

export type ExportChangedPayload = {
  type: 'exportChanged'
  exportChanged: {
    id: number
    what: 'create' | 'update' | 'delete'
  }
}

export type GroupUpdatedPayload = {
  type: 'group'
  group: {
    id: number
  }
}

export const handleBalanceUpdated = (store: MiddlewareAPI, orgId: number | string, payload: BalanceUpdatedPayload) => {
  if (!orgId) {
    return
  }

  store.dispatch(
    esApi.util.updateQueryData('getOrgBalance', { orgId: orgId.toString() }, (balance) => {
      Object.assign(balance, payload.balance)
    }),
  )
}

export const handleCampaignUpdated = (store: MiddlewareAPI, payload: CampaignUpdatedPayload) => {
  if (payload.campaign.clientRef === clientRef) {
    return
  }

  const campaignId = payload.campaign.id
  const state = store.getState()
  const orgId = selectOrgId(state)
  const editedCampaignId = getEditedCampaignId()
  if (!orgId || editedCampaignId !== campaignId) {
    return
  }

  const currentCampaign = esApi.endpoints.getCampaign.select({
    campaignId: campaignId.toString(),
    orgId: orgId.toString(),
  })(state).data
  if (!currentCampaign || currentCampaign.id !== campaignId || currentCampaign.version >= payload.campaign.version) {
    return
  }

  store.dispatch(
    createNotification({
      'campaign-updated': {
        type: 'warning',
        message: OTHER_UPDATED_CAMPAIGN,
      },
    }),
  )
}

const getEditedCampaignId = () => {
  if (typeof window === 'undefined') {
    return undefined
  }

  const match = matchPath('/campaigns/edit/:campaignsId/:step?', window.location.pathname)
  return match ? Number(match.params.campaignsId) : undefined
}

/**
 * This function is used to filter out the org report json file
 */
export const handleExportFiles = (exp: Export | undefined) => {
  if (!exp || !exp.id) {
    return []
  }

  switch (exp.function) {
    case 'aicamp':
      return [] // ignore AI campaign for now

    case 'callrecordings':
    case 'campreport':
    case 'cxupload':
    case 'smsreport':
      return exp.files || []

    case 'orgspend':
      return (exp.files || []).filter((file: BackendFile) => file.contentType === OrgReportMimeType.Spend)

    default:
      return cannot(exp.function)
  }
}

export const createExportNotification = async (
  store: MiddlewareAPI,
  orgId: number,
  token: string,
  exp: Export,
  files: BackendFile[],
) => {
  switch (exp.function) {
    case 'aicamp':
      // Ignore AI campaign for now
      return
    case 'callrecordings':
    case 'campreport':
    case 'cxupload':
    case 'smsreport': {
      const paths = files.map((file: BackendFile) =>
        http.campaigns.exportReport.getSignablePath({
          orgId,
          exportId: exp.id,
          name: file.filename,
        }),
      )

      const data = {
        paths,
      }
      const res = await request.auth.sign({ token, item: data })

      const urls = paths.map((p) => http.campaigns.exportReport.getDownloadPath({ signedPath: p, urlToken: res[p] }))
      const hasMany = urls.length > 1
      const isRecordings = exp.function === 'callrecordings'
      const type = exp.function === 'cxupload' ? 'upload' : 'report'
      store.dispatch(
        createNotification({
          'export-report-success': {
            type: 'success',
            disableAutoHide: true,
            links: urls.map((url, i) => {
              const downloadLabel = hasMany ? `Download ${type} file ${i + 1}.` : `Download ${type} file.`
              return {
                name: `{{link_${i}}}`,
                title: isRecordings ? `Download ${files[i].filename}` : downloadLabel,
                onClick: (e) => {
                  e.preventDefault()
                  window.open(url, '_blank')
                },
              }
            }),
            message: isRecordings
              ? `Your recordings ZIP${hasMany ? 's are' : ' is'} ready. ${urls.map((_, i) => `{{link_${i}}}`).join(' ')}`
              : `Your ${type} is ready. Click the link${
                  hasMany ? 's' : ''
                } below to download the ${type} file${hasMany ? 's' : ''}. ${urls.map((_, i) => `{{link_${i}}}`).join('')}`,
          },
        }),
      )
      return
    }

    case 'orgspend': {
      const urls = files.map((file) => `/reports/file/${exp.id}?filename=${file.filename}`)
      const hasMany = urls.length > 1
      store.dispatch(
        createNotification({
          'export-report-success': {
            type: 'success',
            disableAutoHide: true,
            links: urls.map((url, i) => ({
              name: `{{link_${i}}}`,
              title: !hasMany ? 'View report file.' : `View report file ${i + 1}.`,
              onClick: (e) => {
                e.preventDefault()
                window.open(url, '_self')
              },
            })),
            message: `Your report is ready. Click the link${
              hasMany ? 's' : ''
            } below to view the report file${hasMany ? 's' : ''}. ${urls.map((_, i) => `{{link_${i}}}`).join('')}`,
          },
        }),
      )
      return
    }

    default:
      cannot(exp.function)
  }
}

export const handleExportDone = async (store: MiddlewareAPI, payload: ExportDonePayload) => {
  const isRecordings = payload.export?.function === 'callrecordings'
  const errorMessage = isRecordings ? ERROR_EXPORT_CALL_RECORDINGS : ERROR_EXPORT_CAMPAIGN_REPORT
  try {
    const state = store.getState()
    const orgId = selectOrgId(state)
    const token = cookies.get('authToken')

    if (!orgId || !token || !payload.export?.id) {
      return
    }
    const files = handleExportFiles(payload.export)
    const failed =
      payload.export.status === 'failed' || (isRecordings && payload.export.status === 'ready' && !files.length)

    if (!files.length && !(isRecordings && failed)) {
      return
    }

    store.dispatch(esApi.util.invalidateTags(['OrgExport']))

    if (failed) {
      store.dispatch(
        createNotification({
          'export-report-error': {
            type: 'error',
            message: errorMessage,
          },
        }),
      )
    } else {
      await createExportNotification(store, orgId, token, payload.export, files)
    }
  } catch (error) {
    captureError(error)
    store.dispatch(
      createNotification({
        'export-report-error': {
          type: 'error',
          message: errorMessage,
        },
      }),
    )
  }
}

export const handleExportChanged = (store: MiddlewareAPI, payload: ExportChangedPayload) => {
  const exportId = pathOr<number>(0, ['exportChanged', 'id'], payload)
  const changeReason = pathOr<string>('', ['exportChanged', 'what'], payload)

  if (!exportId) {
    return
  }

  switch (changeReason) {
    case 'delete': {
      store.dispatch(esApi.util.invalidateTags(['OrgExport']))
      store.dispatch(createNotification({})) // clear notifications
      break
    }

    default: // Ignoring create, update for now
      break
  }
}

export const handleGroupUpdated = (store: MiddlewareAPI, payload: GroupUpdatedPayload) => {
  if (!payload.group?.id) {
    return
  }

  store.dispatch(esApi.util.invalidateTags(['OrgGroup', 'OrgGroups']))
}
